import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { IoCallOutline } from "react-icons/io5"
import { MdMailOutline } from "react-icons/md"
import styles from "./quotationAgencyContactHover.module.scss"
import cellStyles from "../../asset/styles/quotation/quotation.module.scss"

const POPOVER_GAP = 10
const CLOSE_DELAY_MS = 150

interface QuotationAgencyContactHoverProps {
    agencyName: string
    agentName: string
    phone: string
    email: string
}

const QuotationAgencyContactHover: React.FC<QuotationAgencyContactHoverProps> = ({
    agencyName,
    agentName,
    phone,
    email,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [position, setPosition] = useState<{
        top: number
        left: number
        isFlipped: boolean
    } | null>(null)

    const triggerRef = useRef<HTMLDivElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const hasContact = Boolean(phone || email)

    const updatePosition = () => {
        const trigger = triggerRef.current
        if (!trigger) return

        const rect = trigger.getBoundingClientRect()
        const popoverWidth = popoverRef.current?.offsetWidth || 240
        const popoverHeight = popoverRef.current?.offsetHeight || 120

        const fitsRight = rect.right + POPOVER_GAP + popoverWidth <= window.innerWidth - 8
        const fitsLeft = rect.left - POPOVER_GAP - popoverWidth >= 8
        const isFlipped = !fitsRight && fitsLeft

        const left = isFlipped
            ? rect.left - POPOVER_GAP - popoverWidth
            : rect.right + POPOVER_GAP

        const top = Math.max(
            8,
            Math.min(
                rect.top + rect.height / 2 - popoverHeight / 2,
                window.innerHeight - popoverHeight - 8,
            ),
        )

        setPosition({ top, left, isFlipped })
    }

    useEffect(() => {
        if (!isOpen) {
            setPosition(null)
            return
        }

        updatePosition()
    }, [isOpen, agencyName, agentName, phone, email])

    useEffect(() => {
        if (!isOpen) return

        const handleReposition = () => updatePosition()
        window.addEventListener("scroll", handleReposition, true)
        window.addEventListener("resize", handleReposition)

        return () => {
            window.removeEventListener("scroll", handleReposition, true)
            window.removeEventListener("resize", handleReposition)
        }
    }, [isOpen])

    useEffect(() => () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }, [])

    const clearCloseTimeout = () => {
        if (!closeTimeoutRef.current) return
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
    }

    const handleOpen = () => {
        clearCloseTimeout()
        if (hasContact) setIsOpen(true)
    }

    const scheduleClose = () => {
        clearCloseTimeout()
        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false)
            closeTimeoutRef.current = null
        }, CLOSE_DELAY_MS)
    }

    if (!agencyName && !agentName) return <span>-</span>

    return (
        <>
            <div
                ref={triggerRef}
                className={[
                    cellStyles.contactInfoCell,
                    styles.contactAnchor,
                    hasContact ? styles.contactTrigger : "",
                ].join(" ")}
                onMouseEnter={handleOpen}
                onMouseLeave={scheduleClose}
            >
                {agencyName && <div className={cellStyles.contactInfoPhone}>{agencyName}</div>}
                {agentName && <div className={cellStyles.contactInfoEmail}>{agentName}</div>}
            </div>

            {isOpen && hasContact && createPortal(
                <div
                    ref={popoverRef}
                    className={[
                        styles.popover,
                        position?.isFlipped ? styles.popoverFlipped : "",
                    ].join(" ")}
                    style={{
                        top: position?.top ?? 0,
                        left: position?.left ?? 0,
                        visibility: position ? "visible" : "hidden",
                    }}
                    onMouseEnter={handleOpen}
                    onMouseLeave={scheduleClose}
                    role="tooltip"
                >
                    <div className={styles.hoverBridge} aria-hidden />
                    {agencyName && <p className={styles.agencyName}>{agencyName}</p>}
                    <div className={styles.divider} />
                    {agentName && <p className={styles.agentName}>{agentName}</p>}
                    {phone && (
                        <div className={styles.contactRow}>
                            <IoCallOutline className={styles.contactIcon} aria-hidden />
                            <a href={`tel:${phone}`} className={styles.contactValue} onClick={(e) => e.stopPropagation()}>
                                {phone}
                            </a>
                        </div>
                    )}
                    {email && (
                        <div className={styles.contactRow}>
                            <MdMailOutline className={styles.contactIcon} aria-hidden />
                            <a href={`mailto:${email}`} className={styles.contactValue} onClick={(e) => e.stopPropagation()}>
                                {email}
                            </a>
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </>
    )
}

export default QuotationAgencyContactHover
